import { useEffect, useRef } from 'react';

export const useScrollAnimation = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Observer pour les éléments scroll-reveal
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            // Déconnecter l'observer après révélation pour optimiser les performances
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observer tous les éléments avec la classe scroll-reveal
    const elements = document.querySelectorAll('.scroll-reveal');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  // Effet de parallaxe optimisé
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrolled = window.pageYOffset;
          const parallaxElements = document.querySelectorAll('.parallax-element');
          
          parallaxElements.forEach((element) => {
            const rate = scrolled * -0.5;
            (element as HTMLElement).style.transform = `translateY(${rate}px)`;
          });
          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Gestion des particules flottantes optimisée
  useEffect(() => {
    let animationId: number;
    
    const createParticle = () => {
      const containers = document.querySelectorAll('.floating-particles');
      
      containers.forEach((container) => {
        if (container.children.length < 12) { // Augmenter légèrement le nombre
          const particle = document.createElement('div');
          particle.className = 'particle';
          particle.style.left = Math.random() * 90 + 5 + '%'; // Éviter les bords
          particle.style.animationDelay = Math.random() * 6 + 's';
          particle.style.animationDuration = (Math.random() * 4 + 10) + 's'; // Durée variable
          container.appendChild(particle);

          // Nettoyer la particule après l'animation avec une marge
          setTimeout(() => {
            if (particle && particle.parentNode) {
              particle.parentNode.removeChild(particle);
            }
          }, 15000); // Temps plus long pour éviter les suppressions prématurées
        }
      });
    };

    // Créer des particules de manière plus fluide
    const scheduleNextParticle = () => {
      const delay = Math.random() * 2000 + 1000; // Entre 1 et 3 secondes
      setTimeout(() => {
        createParticle();
        animationId = requestAnimationFrame(scheduleNextParticle);
      }, delay);
    };
    
    scheduleNextParticle();
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);
};

export default useScrollAnimation;